/**
 * CRITICAL UTILITY — Safe numeric value extraction for MongoDB data.
 * 
 * Database stores numbers in multiple formats:
 * 1. Plain number: spend = 500
 * 2. Object with breakdown: spend = {fb: 200, ig: 150, tg: 150}
 * 
 * ALL frontend code reading daily_data MUST use these safe functions.
 */

/** Convert any value to number. Handles: number, object (sums values), string, null/undefined */
export function safeNum(v) {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return parseFloat(v) || 0;
  if (typeof v === 'object' && !Array.isArray(v)) {
    return Object.values(v).reduce((sum, val) => {
      if (typeof val === 'number') return sum + val;
      if (typeof val === 'string') return sum + (parseFloat(val) || 0);
      return sum;
    }, 0);
  }
  return 0;
}

/** Convert any value to integer */
export function safeInt(v) {
  return Math.round(safeNum(v));
}

/** Sum leads from leadsM + leadsS + leadsIB (handles both dict and plain formats) */
export function sumLeads(row) {
  let total = 0;
  for (const key of ['leadsM', 'leadsS', 'leadsIB']) {
    const val = row?.[key];
    if (val == null) continue;
    if (typeof val === 'object' && !Array.isArray(val)) {
      total += Object.values(val).reduce((s, v) => s + (Number(v) || 0), 0);
    } else {
      total += Number(val) || 0;
    }
  }
  return total;
}

/** Sum closed from newClosed + vipClosed, fallback to closed/totalClosed */
export function sumClosed(row) {
  const nc = safeInt(row?.newClosed);
  const vc = safeInt(row?.vipClosed);
  if (nc > 0 || vc > 0) return nc + vc;
  return safeInt(row?.closed) || safeInt(row?.totalClosed);
}

/** Get total commission MYR from sales row */
export function sumCommissionMyr(row) {
  const top = safeNum(row?.total_commission_myr);
  if (top > 0) return top;
  // Prefer legacy brokerA/B/C when they exist (matches Daily Data TOTAL — only enabled brokers)
  let legacyTotal = 0;
  for (const bk of ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE']) {
    const bd = row?.[bk];
    if (bd && typeof bd === 'object') {
      legacyTotal += safeNum(bd.commission);
    }
  }
  if (legacyTotal > 0) return legacyTotal * 4.2;
  // Fallback to brokers dict (when no legacy keys)
  const brokers = row?.brokers;
  if (brokers && typeof brokers === 'object') {
    let total = 0;
    for (const bd of Object.values(brokers)) {
      if (bd && typeof bd === 'object') {
        total += safeNum(bd.commission_myr);
      }
    }
    if (total > 0) return total;
    for (const bd of Object.values(brokers)) {
      if (bd && typeof bd === 'object') {
        total += safeNum(bd.commission_usd) || safeNum(bd.commission);
      }
    }
    return total * 4.2;
  }
  return 0;
}

/** Get total deposit MYR from sales row */
export function sumDepositMyr(row) {
  const top = safeNum(row?.total_deposit_myr);
  if (top > 0) return top;
  let legacyTotal = 0;
  for (const bk of ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE']) {
    const bd = row?.[bk];
    if (bd && typeof bd === 'object') {
      legacyTotal += safeNum(bd.deposit);
    }
  }
  if (legacyTotal > 0) return legacyTotal * 4.2;
  const brokers = row?.brokers;
  if (brokers && typeof brokers === 'object') {
    let total = 0;
    for (const bd of Object.values(brokers)) {
      if (bd && typeof bd === 'object') {
        total += safeNum(bd.deposit_myr);
      }
    }
    if (total > 0) return total;
    for (const bd of Object.values(brokers)) {
      if (bd && typeof bd === 'object') {
        total += safeNum(bd.deposit_usd) || safeNum(bd.deposit);
      }
    }
    return total * 4.2;
  }
  return 0;
}


/** Get total deposit in USD from sales row */
export function sumDepositUsd(row) {
  const topUsd = safeNum(row?.total_deposit_usd);
  if (topUsd > 0) return topUsd;
  const topMyr = safeNum(row?.total_deposit_myr);
  if (topMyr > 0) return topMyr / (safeNum(row?.exchange_rate_used) || 4.2);
  let legacyTotal = 0;
  for (const bk of ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE']) {
    const bd = row?.[bk];
    if (bd && typeof bd === 'object') {
      legacyTotal += safeNum(bd.deposit);
    }
  }
  if (legacyTotal > 0) return legacyTotal;
  const brokers = row?.brokers;
  if (brokers && typeof brokers === 'object') {
    let total = 0;
    for (const bd of Object.values(brokers)) {
      if (bd && typeof bd === 'object') {
        total += safeNum(bd.deposit_usd) || safeNum(bd.deposit);
      }
    }
    return total;
  }
  return 0;
}

/** Get total commission in USD from sales row */
export function sumCommissionUsd(row) {
  const topUsd = safeNum(row?.total_commission_usd);
  if (topUsd > 0) return topUsd;
  const topMyr = safeNum(row?.total_commission_myr);
  if (topMyr > 0) return topMyr / (safeNum(row?.exchange_rate_used) || 4.2);
  // Prefer legacy brokerA/B/C (only enabled brokers, matches Daily Data)
  let legacyTotal = 0;
  for (const bk of ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE']) {
    const bd = row?.[bk];
    if (bd && typeof bd === 'object') {
      legacyTotal += safeNum(bd.commission);
    }
  }
  if (legacyTotal > 0) return legacyTotal;
  // Fallback to brokers dict
  const brokers = row?.brokers;
  if (brokers && typeof brokers === 'object') {
    let total = 0;
    for (const bd of Object.values(brokers)) {
      if (bd && typeof bd === 'object') {
        total += safeNum(bd.commission_usd) || safeNum(bd.commission);
      }
    }
    return total;
  }
  return 0;
}
